import { useState } from 'react';

import { ChevronDown, Info, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AnimatedCollapsibleContent,
  Collapsible,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

import { AiReportPlan } from '../types/ai-report';

interface ReportAiPlanSummaryProps {
  plan: AiReportPlan;
}

export const ReportAiPlanSummary = ({ plan }: ReportAiPlanSummaryProps) => {
  const { t } = useTranslation('authenticated.reports');
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-3 text-xs">
      <div className="flex items-start gap-2">
        <Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <p className="font-medium">{plan.goal}</p>
      </div>

      {plan.assumptions.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="font-medium text-muted-foreground">{t('ai.plan.assumptions')}</p>
          <ul className="list-disc space-y-0.5 pl-4 text-muted-foreground">
            {plan.assumptions.map(a => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      {plan.metricExplanations.length > 0 && (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="flex w-full items-center gap-1 font-medium text-muted-foreground hover:text-foreground">
            <Info className="h-3.5 w-3.5" />
            <span className="flex-1 text-left">{t('ai.plan.howCalculated')}</span>
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')}
            />
          </CollapsibleTrigger>
          <AnimatedCollapsibleContent>
            <dl className="mt-1.5 flex flex-col gap-1.5">
              {plan.metricExplanations.map(m => (
                <div key={m.key}>
                  <dt className="font-medium">{m.label}</dt>
                  <dd className="text-muted-foreground">{m.explanation}</dd>
                </div>
              ))}
            </dl>
          </AnimatedCollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};
